import { Component, HostListener, Input, ElementRef } from '@angular/core';
import { OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MenuService } from '../../services/menu.service';
import { ThemeIconService } from 'app/services/theme-icon.service';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../types/app-state';
import { SessionService } from 'app/services/session.service';
import { AccountService } from 'app/services/account.service';
import { LocalStorageService } from 'app/services/localStorage.service';
import { MetaService } from 'app/services/meta.service';
import { Globals } from 'app/globals';

@Component({
  selector: 'app-global-header',
  templateUrl: './global-header.component.html',
  standalone: false,
})
export class GlobalHeaderComponent implements OnInit {
  private menuId = 'global-nav-main';
  private supportMenuId = 'help-icon-menu';
  @Input() sidebarMetadata: any;
  menu: any;
  supportMenu: any;
  headerReady = false;
  isResponsiveNav = false;
  theme: string;
  toggles$: Observable<any[]>;
  isUserLoggedIn = true;
  userName: string;
  userMail: string;
  accountSubscription: Subscription = new Subscription();
  metadata: any = null;
  isSidebarPresent = false;
  isDropdownVisible = false;
  private dropdownTimeout: any;
  searchExpanded = false;
  showNavMenu = true;
  collapseTimeout: any;

  constructor(
    private metaService: MetaService,
    private menuService: MenuService,
    private themeIconService: ThemeIconService,
    protected store: Store<AppState>,
    private router: Router,
    private sessionService: SessionService,
    private localStorageService: LocalStorageService,
    private eleRef: ElementRef<HTMLElement>,
    public globals: Globals
  ) {
    this.toggles$ = this.store.pipe(select('toggle'));
  }

  ngOnInit() {
    this.checkScreenSize();
    this.theme = this.themeIconService.getDefaultSetting();
    this.themeIconService.themeInfo.subscribe((theme) => {
      this.theme = theme;
    });
    forkJoin({
      mainMenu: this.menuService.getMenu(this.menuId).pipe(
        catchError((err) => {
          console.error('[GlobalHeader] Failed to load main menu:', err);
          return of({ Items: [] });
        }),
      ),
      supportMenu: this.menuService.getMenu(this.supportMenuId).pipe(
        catchError((err) => {
          console.error('[GlobalHeader] Failed to load support menu:', err);
          return of({ Items: [] });
        }),
      ),
    }).subscribe(({ mainMenu, supportMenu }) => {
      if (mainMenu?.Items && mainMenu.Items.length > 0) {
        this.menu = mainMenu.Items[0].items.map((item) => {
          return {
            ...item,
            external: item.path.trim().search(/(http|https):/) === 0,
          };
        });
      }

      if (supportMenu?.Items && supportMenu.Items.length > 0) {
        this.supportMenu = supportMenu.Items[0].items[0];
      }

      this.headerReady = true;
    });

    this.metaService.metadata.subscribe((meta) => {
      this.isSidebarPresent = meta && meta['sidebar-menu'] === undefined ? false : meta['sidebar-menu'];
      this.metadata = meta;
      if (meta && meta.template && meta.template === 'homepage') {
        this.eleRef.nativeElement.classList.add('-secondaryNav');
      } else {
        this.eleRef.nativeElement.classList.remove('-secondaryNav');
      }
    });

    this.getLoggedInUserInfo();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.checkScreenSize();
  }

  checkScreenSize(): void {
    const isSmallScreen = window.innerWidth <= 1023;
    if (!isSmallScreen) {
      this.isResponsiveNav = false;
    }
  }

  onUserInfoMouseEnter() {
    clearTimeout(this.dropdownTimeout);
    this.isDropdownVisible = true;
  }

  onUserInfoMouseLeave() {
    this.dropdownTimeout = setTimeout(() => {
      this.isDropdownVisible = false;
    }, 400);
  }

  toggleResponsiveMenu() {
    this.isResponsiveNav = !this.isResponsiveNav;
  }

  switchThemeMode() {
    this.theme = this.themeIconService.switchThemeMode(this.theme);
  }

  getLoggedInUserInfo() {
    if (this.sessionService.getUserInfo()) {
      this.isUserLoggedIn = true;
      const userString = this.localStorageService.getItem('user');
      let userInfo = null;
      if (userString) {
        try {
          userInfo = JSON.parse(userString);
        } catch (e) {
          userInfo = null;
        }
      }
      if (userInfo && userInfo['firstName'] && userInfo['lastName'] && userInfo['email']) {
        this.userName = userInfo['firstName'] + ' ' + userInfo['lastName'];
        this.userMail = userInfo['email'];
      }
    } else {
      this.isUserLoggedIn = false;
    }
  }

  onSearchExpand(expanded: boolean) {
    if (expanded) {
      this.showNavMenu = false;
      this.searchExpanded = true;
      if (this.collapseTimeout) {
        clearTimeout(this.collapseTimeout);
      }
    } else {
      this.searchExpanded = false;
      this.collapseTimeout = setTimeout(() => {
        this.showNavMenu = true;
      }, 200);
    }
  }

  get showSkeletonLoader(): boolean {
    const inferredTemplate = this.globals.inferSkeletonTemplate(this.router.url);
    const shouldShowLoaderFromRoute =
      inferredTemplate === this.globals.TEMPLATE_TYPES.REDOC ||
      inferredTemplate === this.globals.TEMPLATE_TYPES.DOC_PAGE;

    return (
      shouldShowLoaderFromRoute &&
      !this.metadata?.template &&
       this.metadata?.['secondary-nav'] !== false &&
       this.metadata?.['sec-nav'] !== false
    );
  }
}
